import React, { useState, useEffect } from 'react';
import { useListState } from '@mantine/hooks';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { NumberInputRow } from '@/components/form/NumberInputRow';
import { FeeAmountFieldset } from './FeeAmountFieldset';
import { useCancellationRuleInputs } from '../../user_configuration/user_invoice/invoice-inputs';
import { cn } from '@/lib/utils';
import { Check, X, Edit, Trash2 } from 'lucide-react';
import { AnimatePresence, motion } from 'framer-motion';
import { EASE_EXPO_OUT, DURATION_FAST, DURATION_MEDIUM, DURATION_SLOW } from '@/lib/motion';

type Rule = { days: number | null; type: 'percentage' | 'fixed'; value: number | null };

interface Props {
  policy: Rule[];
  onChange: (rules: Rule[]) => void;
  paymentTermsDays: number | null;
  onDeactivate?: () => void;
}

interface CancellationRuleItemProps {
  rule: Rule;
  index: number;
  policy: Rule[];
  paymentTermsDays: number | null;
  onRemove: () => void;
  isEditing: boolean;
  onStartEdit: () => void;
  onSave: (rule: Rule) => void;
  onCancel: () => void;
  onValidationChange?: (index: number, isValid: boolean) => void;
  isValid: boolean;
}

interface CancellationRuleDisplayProps {
  rule: Rule;
  index: number;
  onEdit: () => void;
  onRemove: () => void;
}

const CancellationRuleDisplay: React.FC<CancellationRuleDisplayProps> = ({
  rule,
  index,
  onEdit,
  onRemove,
}) => {
  return (
    <motion.div 
      layout
      initial={{ opacity: 0, y: -10 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -10, scale: 0.95 }}
      transition={{ duration: DURATION_MEDIUM, ease: EASE_EXPO_OUT }}
      className="flex items-center justify-between p-3 border rounded-lg bg-muted/10 hover:bg-muted/20 transition-colors cursor-pointer text-sm sm:text-base"
      onClick={onEdit}
    >
      <div className="flex-1">
        <div className="flex items-center gap-2">
          <span className="font-medium">Rule {index + 1}:</span>
          <span>
            {rule.days} day{rule.days !== 1 ? 's' : ''} before →
          </span>
          <span className="font-medium">
            {rule.type === 'percentage' 
              ? `${rule.value}% penalty`
              : `$${rule.value} penalty`
            }
          </span>
        </div>
      </div>
      <div className="flex items-center gap-1">
        <Button
          size="icon-xs"
          variant="ghost"
          onClick={(e) => {
            e.stopPropagation();
            onEdit();
          }}
          aria-label="Edit rule"
          className="text-muted-foreground hover:text-foreground transition-colors"
        >
          <Edit className="h-3 w-3" />
        </Button>
        <Button
          size="icon-xs"
          variant="ghost"
          onClick={(e) => {
            e.stopPropagation();
            onRemove();
          }}
          aria-label="Remove rule"
          className="text-muted-foreground hover:text-destructive transition-colors"
        >
          <Trash2 className="h-3 w-3" />
        </Button>
      </div>
    </motion.div>
  );
};

const CancellationRuleItem = React.memo(React.forwardRef<HTMLDivElement, CancellationRuleItemProps>(({
  rule,
  index,
  policy,
  paymentTermsDays,
  onRemove,
  isEditing,
  onStartEdit,
  onSave,
  onCancel,
  onValidationChange,
  isValid,
}, ref) => {
  const [localRule, setLocalRule] = useState<Rule>(rule);
  const [isClosing, setIsClosing] = useState(false);

  // Update local rule when prop changes (for new rules)
  useEffect(() => {
    setLocalRule(rule);
  }, [rule]);

  const updateLocalRule = (updates: Partial<Rule>) => {
    const newRule = { ...localRule, ...updates };
    setLocalRule(newRule);
    // Don't call onChange here - only update local state during editing
  };

  const handleCancel = () => {
    setLocalRule(rule); // Reset to original
    setIsClosing(true);
    // Delay the actual cancel to allow animation to play
    setTimeout(() => {
      onCancel();
      setIsClosing(false);
    }, 200); // Match the animation duration
  };

  const { daysInput, valuePercentageInput, valueFixedInput } = useCancellationRuleInputs(
    localRule,
    index,
    paymentTermsDays,
    (updates) => {
      setLocalRule(prev => ({ ...prev, ...updates }));
      // Check if all inputs for this rule are valid after update
      const newRule = { ...localRule, ...updates };
      const daysValid = newRule.days !== null && newRule.days >= 1;
      const valueValid = newRule.value !== null && (newRule.type === 'percentage' 
        ? (newRule.value >= 0 && newRule.value <= 100)
        : (newRule.value > 0));
      onValidationChange?.(index, daysValid && valueValid);
    }
  );

  if (!isEditing) {
    return (
      <CancellationRuleDisplay
        rule={rule}
        index={index}
        onEdit={onStartEdit}
        onRemove={onRemove}
      />
    );
  }

  return (
    <motion.div 
      ref={ref}
      layout
      initial={{ opacity: 0, height: 0 }}
      animate={{ opacity: isClosing ? 0 : 1, height: isClosing ? 0 : "auto" }}
      exit={{ opacity: 0, height: 0 }}
      transition={{ duration: isClosing ? DURATION_MEDIUM : DURATION_SLOW, ease: EASE_EXPO_OUT }}
      className="overflow-hidden"
    >
      <motion.div
        initial={{ y: -20, opacity: 0 }}
        animate={{ y: isClosing ? -20 : 0, opacity: isClosing ? 0 : 1 }}
        transition={{ duration: isClosing ? DURATION_FAST : DURATION_MEDIUM, delay: isClosing ? 0 : 0.1, ease: EASE_EXPO_OUT }}
        className="space-y-4 p-4 border rounded-lg bg-muted/20"
      >
        <div className="flex items-center justify-between">
          <Label className="text-sm font-medium">
            {index === policy.length - 1 && rule.days === 1 && rule.value === 50 ? 'New Rule' : `Rule ${index + 1}`}
          </Label>
          <Button
            size="icon-xs"
            variant="ghost"
            onClick={onRemove}
            aria-label="Remove rule"
            className="text-muted-foreground hover:text-destructive transition-colors"
            data-skip-validation="true"
          >
            <Trash2 className="h-3 w-3" />
          </Button>
        </div>

        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: DURATION_MEDIUM, delay: 0.2, ease: EASE_EXPO_OUT }}
        >
          <NumberInputRow
            id={`cancel-days-${index}`}
            label="Days Before Shift"
            description="Minimum days before shift when this surcharge applies"
            unit="days"
            unitPosition="right"
            inputProps={daysInput.inputProps}
            errorElement={daysInput.errorElement}
          />
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: DURATION_MEDIUM, delay: 0.3, ease: EASE_EXPO_OUT }}
        >
          <FeeAmountFieldset
            type={localRule.type}
            onTypeChange={(t) => {
              setLocalRule(prev => ({ ...prev, type: t, value: null }));
            }}
            percentageInputProps={valuePercentageInput.inputProps}
            percentageErrorElement={valuePercentageInput.errorElement}
            fixedInputProps={valueFixedInput.inputProps}
            fixedErrorElement={valueFixedInput.errorElement}
            idPrefix={`cancel-${index}`}
            percentageLabel="Penalty Percentage"
            fixedLabel="Surcharge Amount"
          />
        </motion.div>

        <motion.div 
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: DURATION_MEDIUM, delay: 0.4, ease: EASE_EXPO_OUT }}
          className="flex gap-2 justify-end"
        >
          <Button
            variant="outline"
            onClick={handleCancel}
            data-skip-validation="true"
            size="sm"
            className="transition-all hover:scale-105"
          >
            <X className="w-4 h-4 mr-1" />
            Cancel
          </Button>
          <Button
            onClick={() => onSave(localRule)}
            disabled={!isValid}
            className={cn(
              "transition-all hover:scale-105",
              !isValid && "opacity-50 cursor-not-allowed hover:scale-100"
            )}
            size="sm"
          >
            <Check className="w-4 h-4 mr-1" />
            Save
          </Button>
        </motion.div>
      </motion.div>
    </motion.div>
  );
}));

CancellationRuleItem.displayName = 'CancellationRuleItem';

export const CancellationPolicyConfig: React.FC<Props> = ({
  policy,
  onChange,
  paymentTermsDays,
  onDeactivate,
}) => {
  const [ruleValidationStates, setRuleValidationStates] = useState<Record<number, boolean>>({});
  const [editingRuleIndex, setEditingRuleIndex] = useState<number | null>(null);
  const [workingPolicy, workingPolicyHandlers] = useListState<Rule>(policy);
  const [newlyAddedRuleIndex, setNewlyAddedRuleIndex] = useState<number | null>(null);

  // Initialize validation states for existing rules
  useEffect(() => {
    const initialStates: Record<number, boolean> = {};
    policy.forEach((rule, index) => {
      const daysValid = rule.days !== null && rule.days >= 1;
      const valueValid = rule.value !== null && (rule.type === 'percentage' 
        ? (rule.value >= 0 && rule.value <= 100)
        : (rule.value > 0));
      initialStates[index] = daysValid && valueValid;
    });
    setRuleValidationStates(initialStates);
    workingPolicyHandlers.setState(policy);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- workingPolicyHandlers is a Mantine useListState handler that changes identity each render; only sync when source policy changes
  }, [policy, paymentTermsDays]);

  const handleValidationChange = (index: number, isValid: boolean) => {
    setRuleValidationStates(prev => ({
      ...prev,
      [index]: isValid
    }));
  };

  const addRule = () => {
    const newRule: Rule = { days: 1, type: 'percentage' as const, value: 50 };
    const newIndex = workingPolicy.length;
    workingPolicyHandlers.append(newRule);
    setEditingRuleIndex(newIndex);
    setNewlyAddedRuleIndex(newIndex);
    
    // Check if the new rule's default values are valid
    const daysValid = newRule.days !== null && newRule.days >= 1;
    const valueValid = newRule.value !== null && (newRule.type === 'percentage' 
      ? (newRule.value >= 0 && newRule.value <= 100)
      : (newRule.value > 0));
    const isNewRuleValid = daysValid && valueValid;
    
    setRuleValidationStates(prev => ({
      ...prev,
      [newIndex]: isNewRuleValid
    }));
  };

  const removeRule = (idx: number) => {
    workingPolicyHandlers.remove(idx);
    const newPolicy = workingPolicy.filter((_, i) => i !== idx);
    onChange(newPolicy);
    
    // Check if this was the last existing rule (not a newly added rule)
    // and if so, deactivate the cancellation policy toggle
    const wasLastExistingRule = newPolicy.length === 0 && idx !== newlyAddedRuleIndex;
    if (wasLastExistingRule) {
      onDeactivate?.();
    }
    
    // If we're removing the rule being edited, clear editing state
    if (editingRuleIndex === idx) {
      setEditingRuleIndex(null);
    } else if (editingRuleIndex !== null && editingRuleIndex > idx) {
      // Adjust editing index if we removed a rule before the one being edited
      setEditingRuleIndex(editingRuleIndex - 1);
    }
    
    // Remove validation state for removed rule and reindex remaining rules
    const newValidationStates: Record<number, boolean> = {};
    newPolicy.forEach((_, newIndex) => {
      const oldIndex = newIndex >= idx ? newIndex + 1 : newIndex;
      newValidationStates[newIndex] = ruleValidationStates[oldIndex] ?? false;
    });
    setRuleValidationStates(newValidationStates);
  };


  const startEditingRule = (index: number) => {
    setEditingRuleIndex(index);
    setNewlyAddedRuleIndex(null);
  };

  const saveRule = (index: number, rule: Rule) => {
    workingPolicyHandlers.setItem(index, rule);
    // Save the current working policy to the parent
    onChange(workingPolicy);
    setEditingRuleIndex(null);
    setNewlyAddedRuleIndex(null);
  };

  const cancelEditingRule = (index: number) => {
    const isCancellingNewRule = index === newlyAddedRuleIndex;
    
    if (isCancellingNewRule) {
      // If it's a new rule being cancelled, remove it from the working policy.
      workingPolicyHandlers.remove(index);
      // Also remove its validation state.
      const newValidationStates = { ...ruleValidationStates };
      delete newValidationStates[index];
      setRuleValidationStates(newValidationStates);
    } else {
      // If it's an existing rule, revert the entire working policy to the last saved state.
      // This correctly reverts any unsaved changes on the rule that was being edited.
      workingPolicyHandlers.setState([...policy]);
    }
    
    setEditingRuleIndex(null);
    setNewlyAddedRuleIndex(null);
  };

  return (
    <>
      <div className="space-y-1">
        <Label className="text-base">Cancellation Rules</Label>
        <p className="text-xs text-muted-foreground">
          Set penalties based on how many days before the shift the client cancels
        </p>
      </div>

      <div className="space-y-2">
        <AnimatePresence mode="popLayout">
          {workingPolicy.map((rule, idx) => (
            <CancellationRuleItem
              key={`rule-${idx}`}
              rule={rule}
              index={idx}
              policy={workingPolicy}
              paymentTermsDays={paymentTermsDays}
              onRemove={() => removeRule(idx)}
              isEditing={editingRuleIndex === idx}
              onStartEdit={() => startEditingRule(idx)}
              onSave={(rule) => saveRule(idx, rule)}
              onCancel={() => cancelEditingRule(idx)}
              onValidationChange={handleValidationChange}
              isValid={ruleValidationStates[idx] ?? false}
            />
          ))}
        </AnimatePresence>
      </div>

      <motion.div
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: DURATION_MEDIUM, ease: EASE_EXPO_OUT }}
      >
        <Button
          variant="secondary"
          onClick={addRule} 
          disabled={editingRuleIndex !== null}
          className={cn(
            "transition-all hover:scale-105",
            editingRuleIndex !== null && "opacity-50 cursor-not-allowed hover:scale-100"
          )}
        >
          + Add Rule
        </Button>
      </motion.div>
    </>
  );
};
